import type { ReactNode } from "react";

// Phase 4e brand-visual-rollout (site-wide polish) — shared page
// section heading vocabulary. Establishes the brand identity on every
// customer page: small brand-red eyebrow, italic display heading,
// brand-red underline accent.
//
// Used by:
//   * HomePage section headings (Catalog, Why JDR)
//   * CategoryLandingPage hero band
//   * SearchResultsPage
//   * CartPage
//   * Auth pages
//   * Account + Portal layout
//
// Two surfaces:
//
//   <PageHeader eyebrow="Browse" title="Equipment" /> — light surface
//   <PageHeader eyebrow="Account" title="Sign in" tone="hero" /> — flag-navy
//
// On the hero tone the eyebrow + accent are brand-white-tinted so they
// read on the dark background.
export type PageHeaderTone = "light" | "hero";

export interface PageHeaderProps {
  eyebrow?: string | undefined;
  title: ReactNode;
  description?: ReactNode;
  tone?: PageHeaderTone;
  align?: "left" | "center";
  // Optional element rendered after the description — typically a
  // CTA cluster.
  actions?: ReactNode;
  headingLevel?: 1 | 2;
}

export function PageHeader({
  eyebrow,
  title,
  description,
  tone = "light",
  align = "left",
  actions,
  headingLevel = 1
}: PageHeaderProps): JSX.Element {
  const Heading = headingLevel === 1 ? "h1" : "h2";
  const isHero = tone === "hero";
  const wrapClass = align === "center" ? "items-center text-center" : "items-start";
  return (
    <div className={`flex flex-col gap-1.5 ${wrapClass}`}>
      {eyebrow ? (
        <p
          className={`text-xs font-semibold uppercase tracking-[0.2em] ${
            isHero ? "text-brand-white/70" : "text-brand-red"
          }`}
        >
          {eyebrow}
        </p>
      ) : null}
      <Heading
        className={`font-serif text-2xl font-bold italic tracking-tight md:text-3xl ${
          isHero ? "text-brand-white" : "text-brand-black"
        }`}
      >
        {title}
      </Heading>
      <span
        aria-hidden="true"
        className={`mt-2 inline-block h-0.5 w-12 ${
          isHero ? "bg-brand-white/60" : "bg-brand-red"
        }`}
      />
      {description ? (
        <p
          className={`mt-2 max-w-2xl text-sm sm:text-base ${
            isHero ? "text-brand-white/85" : "text-zinc-600"
          }`}
        >
          {description}
        </p>
      ) : null}
      {actions ? <div className="mt-4">{actions}</div> : null}
    </div>
  );
}
